/**
 * SessionList 组件
 *
 * 会话历史列表下拉组件，提供：
 * - 显示最近20个会话
 * - 会话项显示（标题、时间戳、消息数量）
 * - 点击切换会话
 * - 删除会话（带确认）
 * - 加载更多按钮（分页）
 * - 空状态处理
 * - 导出/导入会话
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useChatStore } from '../../store/chatStore';
import type { ChatSession } from '../../types/chat';
import { formatRelativeTime } from '../../utils/timeFormat';
import ExportImportModal from './ExportImportModal';

// 图标组件
const TrashIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const MessageIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

// 记忆状态图标组件
const MemoryIcon: React.FC<{ className?: string; memorized: boolean }> = ({ className = 'w-3.5 h-3.5', memorized }) => (
  <svg className={className} fill={memorized ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const DownloadIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const UploadIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);

/**
 * SessionList组件属性接口
 */
interface SessionListProps {
  /** 工作区ID */
  workspaceId: number;
  /** JWT认证令牌 */
  token: string;
  /** 是否显示列表 */
  isOpen: boolean;
  /** 关闭列表 */
  onClose: () => void;
  /** 切换会话后的回调 */
  onSessionSwitch?: (sessionId: string) => void;
}

/**
 * 格式化时间戳
 */
const formatTimestamp = (timestamp: string): string => {
  return formatRelativeTime(timestamp);
};

/**
 * SessionList组件
 */
export const SessionList: React.FC<SessionListProps> = ({
  workspaceId,
  token,
  isOpen,
  onClose,
  onSessionSwitch,
}) => {
  const {
    sessions,
    currentSession,
    loadSession,
    deleteSession,
    fetchSessions,
    loading,
    sessionsLoaded,
  } = useChatStore();

  const [page, setPage] = useState(1);
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<ChatSession | null>(null);

  // 导出/导入相关状态
  const [showExportImportModal, setShowExportImportModal] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());

  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * 加载更多会话
   */
  const handleLoadMore = useCallback(async () => {
    if (loading) return;

    const nextPage = page + 1;
    setPage(nextPage);

    try {
      await fetchSessions(token, workspaceId, nextPage, 20);
    } catch (error) {
      console.error('加载更多会话失败:', error);
      setPage(page); // 回退页码
    }
  }, [token, workspaceId, page, loading, fetchSessions]);

  /**
   * 处理会话点击
   */
  const handleSessionClick = useCallback(async (session: ChatSession) => {
    if (loading || session.id === currentSession?.id) {
      onClose();
      return;
    }

    try {
      await loadSession(token, session.id);
      onClose();

      if (onSessionSwitch) {
        onSessionSwitch(session.id);
      }
    } catch (error) {
      console.error('切换会话失败:', error);
    }
  }, [token, currentSession, loading, loadSession, onClose, onSessionSwitch]);

  /**
   * 显示删除确认对话框
   */
  const handleDeleteClick = useCallback((session: ChatSession, e: React.MouseEvent) => {
    console.log('[SessionList] 删除按钮点击 - sessionId:', session.id);
    e.stopPropagation(); // 阻止触发会话切换
    setSessionToDelete(session);
    setShowDeleteConfirm(true);
  }, []);

  /**
   * 确认删除会话
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!sessionToDelete) return;

    console.log('[SessionList] 确认删除 - sessionId:', sessionToDelete.id, 'workspaceId:', workspaceId);
    setDeletingSessionId(sessionToDelete.id);
    setShowDeleteConfirm(false);

    try {
      await deleteSession(token, workspaceId, sessionToDelete.id);
      console.log('[SessionList] 删除成功 - sessionId:', sessionToDelete.id);

      // 如果删除的是当前会话，需要处理
      if (currentSession?.id === sessionToDelete.id) {
        console.log('[SessionList] 删除的是当前会话，需要切换');
        // 切换到最近的会话或创建新会话
        const remainingSessions = sessions.filter(s => s.id !== sessionToDelete.id);
        if (remainingSessions.length > 0) {
          await loadSession(token, remainingSessions[0].id);
        }
      }
    } catch (error) {
      console.error('[SessionList] 删除会话失败:', error);
    } finally {
      setDeletingSessionId(null);
      setSessionToDelete(null);
    }
  }, [token, workspaceId, sessionToDelete, currentSession, sessions, deleteSession, loadSession]);

  /**
   * 取消删除
   */
  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSessionToDelete(null);
  }, []);

  /**
   * 切换会话选择状态
   */
  const handleToggleSelect = useCallback((sessionId: string) => {
    setSelectedSessionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  }, []);

  /**
   * 切换全选
   */
  const handleToggleSelectAll = useCallback(() => {
    if (selectedSessionIds.size === sessions.length) {
      setSelectedSessionIds(new Set());
    } else {
      setSelectedSessionIds(new Set(sessions.map(s => s.id)));
    }
  }, [selectedSessionIds.size, sessions]);

  /**
   * 打开导出/导入模态框
   */
  const handleOpenExportImport = useCallback(() => {
    setShowExportImportModal(true);
  }, []);

  /**
   * 关闭导出/导入模态框
   */
  const handleCloseExportImport = useCallback(() => {
    setShowExportImportModal(false);
  }, []);

  /**
   * 导出/导入成功回调
   */
  const handleExportImportSuccess = useCallback(() => {
    // 刷新会话列表
    fetchSessions(token, workspaceId, 1, 20);
    setSelectedSessionIds(new Set());
  }, [token, workspaceId, fetchSessions]);

  /**
   * 点击外部关闭列表（但不关闭删除确认对话框）
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // 如果正在显示删除确认对话框，不关闭会话列表
      if (showDeleteConfirm) {
        return;
      }

      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // 关闭列表并重置选择状态
        setSelectedSessionIds(new Set());
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, showDeleteConfirm]);

  /**
   * 初始加载会话列表
   * 注意：只在sessions尚未加载时加载，避免重复请求
   */
  useEffect(() => {
    console.log('[SessionList] Effect触发 - isOpen:', isOpen, 'sessionsLoaded:', sessionsLoaded, 'loading:', loading, 'sessions数量:', sessions.length);
    if (isOpen && !sessionsLoaded && !loading) {
      console.log('[SessionList] 开始加载会话列表');
      fetchSessions(token, workspaceId, 1, 20);
    }
  }, [isOpen, sessionsLoaded, loading, token, workspaceId, fetchSessions, sessions.length]);

  /**
   * 关闭列表时重置选择状态（但不重置删除确认状态）
   */
  useEffect(() => {
    if (!isOpen && !showDeleteConfirm) {
      setSelectedSessionIds(new Set());
    }
  }, [isOpen, showDeleteConfirm]);

  // 始终渲染删除确认对话框和导出/导入模态框，即使会话列表关闭
  const renderModals = () => (
    <>
      {/* 删除确认对话框 */}
      {showDeleteConfirm && sessionToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-sm p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">确认删除会话？</h3>
            <p className="text-sm text-gray-700 mb-4">
              您确定要删除会话 "{sessionToDelete.title}" 吗？此操作无法撤销。
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 导出/导入模态框 */}
      <ExportImportModal
        workspaceId={workspaceId}
        token={token}
        isOpen={showExportImportModal}
        onClose={handleCloseExportImport}
        selectedSessionIds={Array.from(selectedSessionIds)}
        onSuccess={handleExportImportSuccess}
      />
    </>
  );

  if (!isOpen) {
    console.log('[SessionList] 不渲染会话列表 - isOpen为false，但可能渲染模态框');
    // 只返回模态框
    return renderModals();
  }

  console.log('[SessionList] 渲染SessionList - sessions数量:', sessions.length, 'loading:', loading);

  return (
    <>
      <div
        ref={containerRef}
        className="fixed w-96 max-h-96 bg-white border border-gray-200 rounded-lg shadow-sm z-50 flex flex-col"
        role="menu"
        aria-label="会话历史列表"
        style={{ top: '80px', right: '20px' }}
      >
        {/* 列表头部 */}
        <div className="px-4 py-3 border-b border-gray-200 bg-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">会话历史</h3>
            <div className="flex items-center gap-2">
              {/* 全选复选框 */}
              {sessions.length > 0 && (
                <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedSessionIds.size === sessions.length && sessions.length > 0}
                    onChange={handleToggleSelectAll}
                    className="rounded border-gray-200 text-gray-700 focus:ring-gray-500"
                  />
                  <span>全选</span>
                </label>
              )}
              {/* 导出/导入按钮 */}
              <div className="flex items-center gap-1">
                <button
                  onClick={handleOpenExportImport}
                  className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                  title="导出会话"
                  aria-label="导出会话"
                >
                  <DownloadIcon />
                </button>
                <button
                  onClick={handleOpenExportImport}
                  className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                  title="导入会话"
                  aria-label="导入会话"
                >
                  <UploadIcon />
                </button>
              </div>
            </div>
          </div>
          {/* 选中计数 */}
          {selectedSessionIds.size > 0 && (
            <div className="mt-2 text-xs text-gray-700">
              已选择 {selectedSessionIds.size} 个会话
            </div>
          )}
        </div>

        {/* 会话列表 */}
        <div className="flex-1 overflow-y-auto">
          {sessions.length === 0 ? (
            // 空状态
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-gray-600">暂无会话历史</p>
              <p className="text-xs text-gray-400 mt-1">点击上方 + 按钮创建新会话</p>
            </div>
          ) : (
            <ul className="py-2">
              {sessions.map((session) => {
                const isActive = session.id === currentSession?.id;
                const isDeleting = deletingSessionId === session.id;
                const isSelected = selectedSessionIds.has(session.id);

                return (
                  <li
                    key={session.id}
                    className={`px-4 py-3 cursor-pointer transition-colors ${
                      isActive
                        ? 'bg-gray-50 border-l-4 border-gray-500'
                        : 'hover:bg-white border-l-4 border-transparent'
                    } ${isSelected ? 'bg-white' : ''}`}
                    role="menuitem"
                    aria-selected={isActive}
                  >
                    <div className="flex items-start gap-3">
                      {/* 选择复选框 */}
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(session.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 rounded border-gray-200 text-gray-700 focus:ring-gray-500"
                        aria-label={`选择会话 ${session.title}`}
                      />

                      {/* 会话内容区域 */}
                      <div
                        className="flex-1 min-w-0"
                        onClick={() => !isDeleting && handleSessionClick(session)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          {/* 会话信息 */}
                          <div className="flex-1 min-w-0">
                            <h4 className={`text-sm font-medium truncate ${
                              isActive ? 'text-gray-900' : 'text-gray-800'
                            }`}>
                              {session.title || '未命名会话'}
                            </h4>

                            {/* 元信息 */}
                            <div className="flex items-center gap-3 mt-1">
                              {/* 时间 */}
                              <div className="flex items-center gap-1 text-xs text-gray-600">
                                <ClockIcon />
                                <span>{formatTimestamp(session.updated_at || session.created_at)}</span>
                              </div>

                              {/* 消息数量 */}
                              {session.message_count !== undefined && (
                                <div className="flex items-center gap-1 text-xs text-gray-600">
                                  <MessageIcon />
                                  <span>{session.message_count} 条消息</span>
                                </div>
                              )}
                            </div>

                            {/* 模型标签 */}
                            {session.model && (
                              <div className="mt-1 flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-50 text-gray-700 rounded">
                                  {session.model}
                                </span>
                                
                                {/* 记忆状态标识 */}
                                <span 
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${
                                    session.is_memorized 
                                      ? 'bg-green-50 text-green-700' 
                                      : 'bg-gray-100 text-gray-500'
                                  }`}
                                  title={session.is_memorized ? '会话已被记忆系统摄取' : '会话等待记忆摄取'}
                                >
                                  <MemoryIcon memorized={session.is_memorized || false} />
                                  {session.is_memorized ? '已记忆' : '待记忆'}
                                </span>
                              </div>
                            )}
                          </div>

                        {/* 删除按钮 */}
                        <button
                          onClick={(e) => handleDeleteClick(session, e)}
                          disabled={isDeleting}
                          className={`p-1.5 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            isActive
                              ? 'text-red-300 hover:text-red-400 hover:bg-red-100 cursor-not-allowed'
                              : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                          }`}
                          title={isActive ? "无法删除当前正在使用的会话" : "删除会话"}
                          aria-label={`删除会话 ${session.title}`}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* 加载更多按钮 */}
        {sessions.length >= 20 && sessions.length % 20 === 0 && (
          <div className="px-4 py-3 border-t border-gray-200">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="w-full px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '加载中...' : '加载更多'}
            </button>
          </div>
        )}
      </div>

      {/* 渲染模态框 */}
      {renderModals()}
    </>
  );
};

export default SessionList;
